/**
 * Scheduling — admin-defined custom "Caregiver Context" values.
 *
 * Administration -> Customization -> In-Home Visit Context. Gated on
 * PRIVILEGES.ADJUST_PERMISSIONS (8) both here and server-side — the same gate
 * AdministrationPage.vue already uses for Groups/Privileges/Break-Glass, since
 * this is the same class of org-wide admin surface.
 *
 * Every saved entry is appended to the Context dropdown in the Scheduling ->
 * Appointments booking flow (appointmentApiService.ts's CAREGIVER_CONTEXTS)
 * and is treated as 'Out-of-Home Medical' for compatibility-matrix purposes —
 * see rubricContext() in appointmentApiService.ts.
 *
 * Contract mirrors the migration:
 *   db/migrations/mysql/20260812073317_scheduling_custom_contexts.sql
 */

import axios from "axios";
import { CURR_API } from "@/constants/apis";

const WEBHOOK = `${CURR_API}/tswebhook.php`;

export const CUSTOM_CONTEXT_LABEL_MAX = 100;

async function post(node) {
  const res = await axios.post(
    WEBHOOK,
    { SchedulingCustomization: node },
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
}

/**
 * All custom contexts for the account, oldest first. Read-only and NOT
 * privilege-gated — every caregiver booking an appointment needs these to
 * populate their Context dropdown, not just admins.
 */
export async function getCustomSchedulingContexts({ accountnumber }) {
  return post({
    API_Meth: "GetCustomSchedulingContexts",
    accountnumber,
  });
}

/** Add one custom context. Duplicate labels (per account) come back as an error, not a silent no-op. */
export async function addCustomSchedulingContext({ accountnumber, actorId, actorName, label }) {
  return post({
    API_Meth: "AddCustomSchedulingContext",
    accountnumber,
    actor_id: actorId,
    actor_name: actorName,
    label,
  });
}

/**
 * Remove a custom context. Hard delete — nothing else references this table
 * by id (an already-booked appointment keeps its context as a plain text
 * snapshot), so removing it here is exactly "gone from the DB and the
 * dropdown", no orphan cleanup needed elsewhere.
 */
export async function deleteCustomSchedulingContext({ accountnumber, actorId, actorName, id }) {
  return post({
    API_Meth: "DeleteCustomSchedulingContext",
    accountnumber,
    actor_id: actorId,
    actor_name: actorName,
    id,
  });
}
